import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Collapse,
  Chip,
  Tooltip,
} from "@mui/material";
import { encryptField, decryptField } from "../utils/encryption";

import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "../firebase";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import LockIcon from "@mui/icons-material/Lock";
import SaveIcon from "@mui/icons-material/Save";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import LogoutIcon from "@mui/icons-material/Logout";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  signOut,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { usePreferences } from "../contexts/PreferencesContext";
import { useBankLinkStatus } from "../hooks/useBankLinkStatus";
import { getBankLink } from "../services/bank";
import { auth, db } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  collection,
  deleteDoc,
  deleteField,
} from "firebase/firestore";
import api from "../services/api";

export default function Settings() {
  const passwordRef = useRef();
  const navigate = useNavigate();
  const uid = auth.currentUser?.uid;

  const roundTo5Minutes = (date) => {
    const ms = 1000 * 60 * 5;
    return new Date(Math.round(date.getTime() / ms) * ms);
  };

  const [showPassword, setShowPassword] = useState(false);
  const [banks, setBanks] = useState([]);
  const [frequency, setFrequency] = useState(new Date());
  const [sendTime, setSendTime] = useState(roundTo5Minutes(new Date()));
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
  const {
    accountantEmail,
    setAccountantEmail,
    deliveryMethod,
    setDeliveryMethod,
    companyName,
    setCompanyName,
    companyAddress,
    setCompanyAddress,
    companyNumber,
    setCompanyNumber,
    refreshPreferences,
  } = usePreferences();

  const [ccUser, setCcUser] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [reminderEmail, setReminderEmail] = useState(true);
  const [selectedBank, setSelectedBank] = useState(null);
  const [connectingBank, setConnectingBank] = useState(false);
  const [reminderBanner, setReminderBanner] = useState(true);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [lastSent, setLastSent] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [bankInfo, setBankInfo] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [plan, setPlan] = useState("free");
  const isPro = plan === "pro" || plan === "proplus";
  const isProPlus = plan === "proplus";
  const [trialStartedAt, setTrialStartedAt] = useState(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState(0);
  const [renewalDate, setRenewalDate] = useState(null);
  const [subscriptionAmount, setSubscriptionAmount] = useState(null);
  const [subscriptionCurrency, setSubscriptionCurrency] = useState("eur");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [bankConnected, setBankConnected] = useState(false);
  const [reauthLoading, setReauthLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reauthPassword, setReauthPassword] = useState("");
  const [showReauthDialog, setShowReauthDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [bankDetails, setBankDetails] = useState("");
  const [requisitionIdFromRedirect, setRequisitionIdFromRedirect] =
    useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");

  const isBankAccessAllowed = isProPlus;

  const {
    status: bankStatus,
    isLoading: bankLoading,
    error: bankError,
  } = useBankLinkStatus(requisitionIdFromRedirect, !!requisitionIdFromRedirect);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");

    if (ref) {
      setRequisitionIdFromRedirect(ref);
      window.history.replaceState({}, document.title, window.location.pathname);

      setTimeout(() => {
        const el = document.getElementById("bank-connection-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, []);

  useEffect(() => {
    const syncTransactions = async () => {
      if (!requisitionIdFromRedirect) return;

      try {
        const res = await api.get(
          `/bank/transactions-sync/${requisitionIdFromRedirect}`,
        );

        setSnackbar({
          open: true,
          message: "✅ Bank account linked and synced!",
          severity: "success",
        });
      } catch (err) {
        console.error("❌ Sync failed:", err);
        setSnackbar({
          open: true,
          message: "❌ Bank linked but failed to sync transactions.",
          severity: "error",
        });
      } finally {
        setBankConnected(true);
        reloadUserData(); // Always pull latest user doc regardless
      }
    };

    if (bankStatus === "connected") {
      syncTransactions();
    }

    if (bankStatus === "failed") {
      setSnackbar({
        open: true,
        message: "❌ Failed to link bank account. Please try again.",
        severity: "error",
      });
    }
  }, [bankStatus, requisitionIdFromRedirect]);

  const formValid =
    deliveryMethod !== "auto" ||
    (deliveryMethod === "auto" &&
      accountantEmail.includes("@") &&
      frequency instanceof Date);

  useEffect(() => {
    const loadBanks = async () => {
      try {
        const res = await api.get("/bank/institutions?country=IE");
        setBanks(res.data);
      } catch (err) {
        console.error("Failed to fetch institutions", err);
      }
    };
    loadBanks();
  }, []);

  useEffect(() => {
    if (banks.length > 0) {
      setSelectedBank((prev) => prev || banks[0].id);
    }
  }, [banks]);

  useEffect(() => {
    if (!uid) return;
    const load = async () => {
      const ref = doc(db, "users", uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;

      const data = snap.data();
      const prefs = data.preferences || {};
      const subscriptionStatus = data.subscriptionStatus || null;
      setSubscriptionStatus(subscriptionStatus);
      const bankData = data.bankConnection || null;

      if (bankData) {
        if (bankData.connected && bankData.accountIds?.length > 0) {
          setBankConnected(true);
          setBankInfo({
            name: bankData.institutionName || "Your Bank",
            connectedAt: bankData.linkedAt
              ? new Date(bankData.linkedAt).toLocaleString()
              : null,
          });
        } else {
          setBankConnected(false);
          setBankInfo({
            name: bankData.institutionName || "Your Bank",
            connectedAt: null,
            error: "⚠️ No accounts were linked during setup. Please reconnect.",
          });
        }
      }

      setSelectedBank(prefs.selectedBank || "AIBKIE2D");
      const trialStart = data.trialStartedAt?.toDate?.();
      const userPlan = data.plan || "free";
      setPlan(userPlan);

      if (trialStart instanceof Date) {
        const now = new Date();
        const daysUsed = Math.floor((now - trialStart) / (1000 * 60 * 60 * 24));
        setTrialDaysLeft(Math.max(0, 14 - daysUsed));
      }

      setTrialStartedAt(trialStart);

      if (isPro) {
        const token = await auth.currentUser.getIdToken();
        const res = await api.get(`/stripe/subscription-status?uid=${uid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const timestamp = res.data?.nextBillingDate;

        if (timestamp) {
          const renewalDate = new Date(timestamp);
          setRenewalDate(renewalDate);
        }

        if (res.data?.amount != null) {
          setSubscriptionAmount(res.data.amount);
          setSubscriptionCurrency(res.data.currency || "eur");
        }
      }

      setDeliveryMethod(prefs.deliveryMethod || "manual");
      setFrequency(prefs.deliveryFrequency?.toDate?.() || new Date());
      setSendTime(prefs.sendTime?.toDate?.() || roundTo5Minutes(new Date()));
      setCcUser(prefs.ccUserOnReport === true);
      setReminderEmail(prefs.reminderEmail !== false);
      setReminderBanner(prefs.reminderBanner !== false);
      setLastSent(data.lastReportSentAt);
      setLogoPreviewUrl(prefs.logoUrl || "");
      setCompanyName(prefs.companyName || "");
      setBankDetails(prefs.bankDetails ? decryptField(prefs.bankDetails) : "");
      await refreshPreferences();
    };
    load();
  }, [uid]);

  const reloadUserData = async () => {
    if (!uid) return;
    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;

    const data = snap.data();
    const bankData = data.bankConnection || null;

    if (bankData?.connected && bankData.accountIds?.length > 0) {
      setBankConnected(true);
      setBankInfo({
        name: bankData.institutionName || "Your Bank",
        connectedAt: bankData.linkedAt
          ? new Date(bankData.linkedAt).toLocaleString()
          : null,
      });
    }
  };

  useEffect(() => {
    if (showReauthDialog) {
      setTimeout(() => {
        passwordRef.current?.focus();
      }, 100); // slight delay to ensure DOM is ready
    }
  }, [showReauthDialog]);

  const handleSave = async () => {
    if (!uid) return;
    setSaving(true);
    try {
      const userDocRef = doc(db, "users", uid);
      let uploadedLogoUrl = logoPreviewUrl; // fallback to preview URL if no new file

      if (logoFile) {
        // Upload logo file to Firebase Storage
        const fileRef = ref(storage, `logos/${uid}`);
        await uploadBytes(fileRef, logoFile);
        uploadedLogoUrl = await getDownloadURL(fileRef);
      }

      const preferencesPayload = {
        accountantEmail: encryptField(accountantEmail),
        deliveryMethod,
        deliveryFrequency: deliveryMethod === "auto" ? frequency : null,
        sendTime: deliveryMethod === "auto" ? sendTime : null,
        ccUserOnReport: ccUser,
        reminderEmail,
        reminderBanner,
        selectedBank,
        logoUrl: uploadedLogoUrl, // ✅ Save only the Storage URL
        companyName,
        companyAddress: encryptField(companyAddress),
        companyNumber: encryptField(companyNumber),
        bankDetails: encryptField(bankDetails),
      };

      // Soft validation warning for Pro users selecting auto-delivery
      if (
        deliveryMethod === "auto" &&
        (!accountantEmail.includes("@") || !(frequency instanceof Date))
      ) {
        setSnackbar({
          open: true,
          message:
            "⚠️ Please enter a valid accountant email and schedule to enable auto-delivery.",
          severity: "warning",
        });
      }

      await setDoc(
        userDocRef,
        { preferences: preferencesPayload },
        { merge: true },
      );

      setSnackbar({
        open: true,
        message: "✅ Settings saved successfully.",
        severity: "success",
      });

      setLogoPreviewUrl(uploadedLogoUrl);
      setLogoFile(null);
      await refreshPreferences();
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "❌ Failed to save settings.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnectBank = async () => {
    if (!uid) return;
    try {
      const ref = doc(db, "users", uid);
      await setDoc(
        doc(db, "users", uid),
        {
          bankConnectedAt: null,
          bankConnection: deleteField(),
        },
        { merge: true },
      );

      setBankConnected(false);
      setBankInfo(null);
      setSnackbar({
        open: true,
        message: "✅ Bank connection removed.",
        severity: "info",
      });
    } catch (err) {
      console.error("Disconnect error:", err);
      setSnackbar({
        open: true,
        message: "❌ Failed to disconnect bank.",
        severity: "error",
      });
    }
  };

  const handleShowDiscount = () => {
    // Option A: Navigate to pricing page
    window.location.href = "/pricing";

    // Option B: Set a flag to show a separate discount dialog/modal
    // setShowDiscountModal(true);
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const token = await auth.currentUser.getIdToken();
      await api.post(
        "/stripe/cancel-subscription",
        { uid },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSubscriptionStatus("cancel_at_period_end");

      setSnackbar({
        open: true,
        message:
          "✅ Subscription will cancel at the end of the billing period.",
        severity: "info",
      });
      setCancelSuccess(true);
    } catch (err) {
      console.error("Cancel failed", err);
      setSnackbar({
        open: true,
        message: "❌ Failed to cancel subscription.",
        severity: "error",
      });
    } finally {
      setCancelling(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await api.post(
        "/stripe/create-portal-session",
        { uid },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.location.href = res.data.url;
    } catch (err) {
      console.error("Billing portal error:", err);
      setSnackbar({
        open: true,
        message: "❌ Could not open billing portal. Please try again.",
        severity: "error",
      });
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    await signOut(auth);
    navigate("/login");
  };

  const handleUpgrade = async () => {
    const source = "settings";
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await api.post(
        "/stripe/create-checkout-session",
        { uid, email: auth.currentUser.email, source, planType: "pro" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.location.href = res.data.url;
    } catch (err) {
      console.error("Upgrade error:", err);
      setSnackbar({
        open: true,
        message: "❌ Failed to redirect to checkout.",
        severity: "error",
      });
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!uid) return;
    setDeleting(true);
    try {
      // 1. Save feedback before deleting account
      await setDoc(
        doc(db, "deletionFeedback", uid),
        {
          reason: deleteReason,
          extraText: customReason,
          deletedAt: serverTimestamp(),
          email: auth.currentUser?.email || "",
          isPro,
          trialStartedAt,
        },
        { merge: true },
      );

      // 2. Delete user record and auth account
      await deleteDoc(doc(db, "users", uid));
      await auth.currentUser.delete();
      navigate("/signup");
    } catch (err) {
      if (err.code === "auth/requires-recent-login") {
        setShowReauthDialog(true);
      } else {
        setSnackbar({
          open: true,
          message: "❌ Failed to delete account.",
          severity: "error",
        });
      }
    } finally {
      setConfirmDelete(false);
      setDeleting(false);
    }
  };

  const handleReauthAndDelete = async () => {
    if (!reauthPassword) {
      setSnackbar({
        open: true,
        message: "❌ Please enter your password.",
        severity: "warning",
      });
      return;
    }

    setReauthLoading(true);
    try {
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        reauthPassword,
      );
      await reauthenticateWithCredential(auth.currentUser, credential);
      await setDoc(
        doc(db, "deletionFeedback", uid),
        {
          reason: deleteReason,
          extraText: customReason,
          deletedAt: serverTimestamp(),
          email: auth.currentUser?.email || "",
          isPro,
          trialStartedAt,
        },
        { merge: true },
      );

      await deleteDoc(doc(db, "users", uid));
      await auth.currentUser.delete();
      navigate("/signup");
    } catch (err) {
      console.error("Reauth failed:", err);
      setSnackbar({
        open: true,
        message:
          err.code === "auth/wrong-password"
            ? "❌ Incorrect password."
            : "❌ Reauthentication failed.",
        severity: "error",
      });
    } finally {
      setReauthLoading(false);
      setShowReauthDialog(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 6, pb: 12 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        ⚙️ Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Manage report delivery, reminders, and account preferences.
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          position: "sticky",
          top: 64,
          zIndex: 100,
          background: "white",
          py: 2,
          mb: 2,
        }}
      >
        <Tooltip
          title={
            !formValid
              ? "Please enter a valid email and delivery deliveryMethod."
              : ""
          }
          arrow
          disableHoverListener={formValid}
        >
          <span>
            <Button
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Grid container spacing={4}>
        {/* LEFT SIDE */}
        <Grid xs={12} md={6}>
          {/* Report Delivery */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              📧 Report Delivery
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Automatically send reports to your accountant.
            </Typography>
            <TextField
              fullWidth
              label="Accountant's Email"
              type="email"
              value={accountantEmail}
              onChange={(e) => setAccountantEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            {deliveryMethod === "auto" &&
              isPro &&
              !accountantEmail.includes("@") && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: -1, mb: 1, display: "block" }}
                >
                  Please enter a valid email address for automatic delivery.
                </Typography>
              )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Delivery deliveryMethod</InputLabel>
              <Tooltip
                title={!isPro ? "Available on Pro Plan only" : ""}
                arrow
                disableHoverListener={isPro}
              >
                <Select
                  value={deliveryMethod}
                  onChange={(e) => {
                    const selected = e.target.value;
                    if (selected === "auto" && !isPro) {
                      setShowUpgradePrompt(true);
                    } else {
                      setDeliveryMethod(selected);
                    }
                  }}
                  label="Delivery deliveryMethod"
                >
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem
                    value="auto"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    {!isPro && (
                      <LockIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.disabled" }}
                      />
                    )}
                    Automatic {!isPro && " (Pro only)"}
                  </MenuItem>
                </Select>
              </Tooltip>
            </FormControl>

            {deliveryMethod === "auto" && isPro && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Day to Auto-Send"
                  views={["day"]}
                  value={frequency}
                  onChange={(newDate) => newDate && setFrequency(newDate)}
                  slotProps={{ textField: { fullWidth: true, sx: { mb: 2 } } }}
                />
                <TimePicker
                  label="Time to Auto-Send"
                  value={sendTime}
                  onChange={(newTime) => newTime && setSendTime(newTime)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            )}
          </Paper>

          {/* Reminders */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              🔔 Reminders
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={ccUser}
                  onChange={(e) => setCcUser(e.target.checked)}
                />
              }
              label="CC me on sent reports"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={reminderEmail}
                  onChange={(e) => setReminderEmail(e.target.checked)}
                />
              }
              label="Send email reminders"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={reminderBanner}
                  onChange={(e) => setReminderBanner(e.target.checked)}
                />
              }
              label="Show in-app reminders"
            />
            {lastSent && (
              <Typography mt={2} variant="body2" color="text.secondary">
                ✅ Last report sent:{" "}
                {new Date(
                  lastSent.toDate ? lastSent.toDate() : lastSent,
                ).toLocaleString()}
              </Typography>
            )}
          </Paper>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mt: 4 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              🏢 Company Information
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              This information will appear at the top of all invoices.
            </Typography>

            {logoPreviewUrl && (
              <Box
                component="img"
                src={logoPreviewUrl}
                alt="Company Logo"
                sx={{ maxHeight: 80, mb: 2 }}
              />
            )}

            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ textTransform: "none", fontWeight: 500, mb: 2 }}
            >
              Upload Logo
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setLogoFile(file);
                    setLogoPreviewUrl(URL.createObjectURL(file));
                  }
                }}
              />
            </Button>

            <TextField
              fullWidth
              label="Company Name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Company Number"
              value={companyNumber}
              onChange={(e) => setCompanyNumber(e.target.value)}
              margin="normal"
            />

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Company Address
            </Typography>
            <TextareaAutosize
              minRows={2}
              placeholder="Enter company address"
              value={companyAddress}
              onChange={(e) => setCompanyAddress(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #ccc",
                fontSize: "1rem",
                fontFamily: "inherit",
              }}
            />
          </Paper>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mt: 4 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              🏦 Bank Payment Details
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              These bank details will appear at the bottom of your invoices.
            </Typography>

            <TextareaAutosize
              minRows={3}
              placeholder="e.g. IBAN: IE00 XXXX XXXX XXXX XX"
              value={bankDetails}
              onChange={(e) => setBankDetails(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #ccc",
                fontSize: "1rem",
                fontFamily: "inherit",
              }}
            />
          </Paper>
        </Grid>

        {/* RIGHT SIDE */}
        <Grid xs={12} md={6}>
          {/* Subscription */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              💳 Subscription
            </Typography>
            {isPro ? (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  You are on the{" "}
                  <strong>
                    {plan === "proplus"
                      ? "ProPlus Plan"
                      : plan === "pro"
                        ? "Pro Plan"
                        : "Free Plan"}
                  </strong>
                  .
                </Typography>

                {subscriptionStatus === "past_due" && (
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      background: "#fff7ed",
                      border: "1px solid #fed7aa",
                      color: "#c2410c",
                      fontWeight: 500,
                    }}
                  >
                    ⚠️ Your last payment failed. Please update your payment
                    details to keep your Pro access.{" "}
                    <Button
                      size="small"
                      variant="text"
                      sx={{ color: "#c2410c", fontWeight: 700, p: 0, ml: 0.5, textTransform: "none" }}
                      onClick={handleManageBilling}
                    >
                      Update payment method →
                    </Button>
                  </Typography>
                )}

                <Box display="flex" alignItems="center" gap={2}>
                  <Chip
                    label={subscriptionStatus === "past_due" ? "Payment Failed" : "Active"}
                    color={subscriptionStatus === "past_due" ? "warning" : "success"}
                    size="small"
                  />
                  {subscriptionStatus !== "cancel_at_period_end" && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setConfirmCancelOpen(true)}
                      disabled={cancelling}
                    >
                      {cancelling ? "Cancelling..." : "Cancel Subscription"}
                    </Button>
                  )}
                </Box>

                {renewalDate && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    🔄 Next billing date:{" "}
                    <strong>{renewalDate.toLocaleDateString()}</strong>
                    {subscriptionAmount != null && (
                      <>
                        {" · "}
                        <strong>
                          {new Intl.NumberFormat(undefined, {
                            style: "currency",
                            currency: subscriptionCurrency,
                          }).format(subscriptionAmount)}
                        </strong>
                      </>
                    )}
                  </Typography>
                )}

                {subscriptionStatus === "cancel_at_period_end" && (
                  <Typography
                    variant="body2"
                    color="warning.main"
                    sx={{ mt: 1 }}
                  >
                    ⚠️ Your subscription will be cancelled at the end of this
                    billing period.
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  You're currently on a <strong>Free Trial</strong>.{" "}
                  {trialDaysLeft} days remaining.
                </Typography>
                <Chip
                  label={`Trial – ${trialDaysLeft} days left`}
                  color="warning"
                  size="small"
                  sx={{ mb: 2 }}
                />
                <Button variant="contained" fullWidth onClick={handleUpgrade}>
                  Upgrade to Pro
                </Button>
              </>
            )}
          </Paper>
          {isProPlus && (
            <Tooltip
              title={
                isBankAccessAllowed
                  ? ""
                  : "🔒 Bank connection is only available on the Pro plan."
              }
              arrow
            >
              <span>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    mb: 4,
                    opacity: isBankAccessAllowed ? 1 : 0.5,
                    pointerEvents: isBankAccessAllowed ? "auto" : "none",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="h6" fontWeight="bold">
                      🏦 Bank Connection
                    </Typography>
                    {!isBankAccessAllowed && (
                      <LockIcon fontSize="small" color="disabled" />
                    )}
                  </Box>

                  {!bankConnected && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Connect your bank account to help match receipts with
                      transactions. This is not required and you can manage
                      everything manually if you prefer.
                    </Typography>
                  )}

                  {bankConnected && bankInfo ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Chip
                        label="Connected ✅"
                        color="success"
                        sx={{ mb: 1 }}
                      />
                      {bankInfo.connectedAt && (
                        <Typography variant="body2" gutterBottom>
                          <strong>{bankInfo.name}</strong> connected on{" "}
                          <em>{bankInfo.connectedAt}</em>
                        </Typography>
                      )}
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setConfirmDisconnectOpen(true)}
                        sx={{ mt: 1 }}
                      >
                        Disconnect Bank
                      </Button>
                    </Box>
                  ) : (
                    <>
                      {bankStatus === "failed" && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          We couldn’t detect any accounts during your last bank
                          connection. You can reconnect below to enable
                          automatic receipt matching. This step is optional.
                        </Alert>
                      )}

                      {bankInfo?.error &&
                        !["No accounts linked", "Session expired"].includes(
                          bankInfo.error,
                        ) && <Alert severity="error">{bankInfo.error}</Alert>}

                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Bank</InputLabel>
                        <Select
                          value={selectedBank}
                          label="Bank"
                          onChange={(e) => setSelectedBank(e.target.value)}
                        >
                          {banks.map((bank) => (
                            <MenuItem key={bank.id} value={bank.id}>
                              {bank.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Box mt={1}>
                        <Button
                          variant="contained"
                          fullWidth
                          disabled={connectingBank}
                          startIcon={
                            connectingBank ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <AccountBalanceWalletIcon />
                            )
                          }
                          sx={{
                            mt: 1,
                            py: 1.25,
                            fontWeight: 600,
                            borderRadius: 2,
                            textTransform: "none",
                            backgroundColor: connectingBank
                              ? "grey.300"
                              : "primary.main",
                            color: connectingBank
                              ? "text.disabled"
                              : "primary.contrastText",
                            "&:hover": {
                              backgroundColor: connectingBank
                                ? "grey.400"
                                : "primary.dark",
                            },
                          }}
                          onClick={async () => {
                            setConnectingBank(true);
                            try {
                              const link = await getBankLink(selectedBank, uid);
                              window.location.href = link;
                            } catch (err) {
                              console.error("Bank connect error:", err);
                              setSnackbar({
                                open: true,
                                message: "❌ Failed to connect to bank.",
                                severity: "error",
                              });
                            } finally {
                              setConnectingBank(false);
                            }
                          }}
                        >
                          {connectingBank ? "Connecting..." : "Connect Bank"}
                        </Button>
                      </Box>
                    </>
                  )}
                </Paper>

                {!isBankAccessAllowed && (
                  <Box sx={{ textAlign: "center", mt: -2, mb: 3 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleUpgrade}
                      sx={{ textTransform: "none" }}
                    >
                      Upgrade to Pro to enable Bank Sync →
                    </Button>
                  </Box>
                )}
              </span>
            </Tooltip>
          )}

          {/* Danger Zone */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderColor: "error.main",
              backgroundColor: "#fff5f5",
              borderRadius: 3,
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              color="error"
              gutterBottom
            >
              ⚠️ Danger Zone
            </Typography>
            <Button
              variant="contained"
              color="error"
              fullWidth
              onClick={() => setConfirmDelete(true)}
              startIcon={<DeleteForeverIcon />}
            >
              Delete My Account
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>We're Sad to See You Go</DialogTitle>
        <DialogContent>
          <Typography>
            We'd love to understand why you're leaving. Your feedback helps us
            improve.
          </Typography>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Reason for leaving</InputLabel>
            <Select
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              label="Reason for leaving"
            >
              <MenuItem value="missing-features">
                Missing features I need
              </MenuItem>
              <MenuItem value="bugs">Too many bugs or errors</MenuItem>
              <MenuItem value="too-expensive">Too expensive</MenuItem>
              <MenuItem value="no-longer-needed">No longer need it</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          {deleteReason === "other" && (
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Let us know more..."
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              sx={{ mt: 2 }}
            />
          )}

          <Collapse
            in={deleteReason === "too-expensive"}
            timeout="auto"
            unmountOnExit
          >
            <Box sx={{ mt: 2 }}>
              <Alert severity="info">
                💡 Did you know we offer a <strong>free plan</strong> and
                discounts?
                <Button size="small" onClick={handleShowDiscount}>
                  Learn More
                </Button>
              </Alert>
            </Box>
          </Collapse>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirmed}
            color="error"
            disabled={deleting}
            startIcon={
              deleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteForeverIcon />
              )
            }
          >
            {deleting ? "Deleting..." : "Delete My Account"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmCancelOpen}
        onClose={() => setConfirmCancelOpen(false)}
      >
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel your Pro subscription? You’ll keep
            access until the end of the billing period.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancelOpen(false)}>Keep Pro</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              setConfirmCancelOpen(false);
              await handleCancelSubscription();
            }}
          >
            Confirm Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
      >
        <DialogTitle>Upgrade to Pro</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            🔒 <strong>Automatic delivery</strong> is only available on the Pro
            plan. Upgrade to unlock scheduled report sending directly to your
            accountant.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUpgradePrompt(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpgrade}>
            Upgrade Now
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showReauthDialog}
        onClose={() => setShowReauthDialog(false)}
      >
        <DialogTitle>Re-authenticate</DialogTitle>
        <DialogContent>
          <TextField
            label="Email"
            fullWidth
            sx={{ mb: 2 }}
            value={auth.currentUser?.email || ""}
            disabled
          />
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Please enter your password to confirm account deletion.
          </Typography>

          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            fullWidth
            value={reauthPassword}
            onChange={(e) => setReauthPassword(e.target.value)}
            inputRef={passwordRef}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    size="small"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReauthDialog(false)}>Cancel</Button>
          <Button
            onClick={handleReauthAndDelete}
            color="error"
            disabled={!reauthPassword || reauthLoading}
            startIcon={
              reauthLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {reauthLoading ? "Processing..." : "Confirm & Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDisconnectOpen}
        onClose={() => setConfirmDisconnectOpen(false)}
      >
        <DialogTitle>Disconnect Bank</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to disconnect your bank account? You'll lose
            the ability to match receipts to transactions.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisconnectOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setConfirmDisconnectOpen(false);
              await handleDisconnectBank();
            }}
            color="error"
            variant="contained"
          >
            Confirm Disconnect
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
